<script setup>
import { computed, ref, inject } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useMenu } from "../../composables/useMenu.js";
import { hasPermission, COLLABORATOR_PERMISSIONS } from "../../composables/usePermissions.js";
import { useShareModal } from "../../composables/useShareModal.js";
import { useProjectsByTab } from "../../composables/useProjectsByTab.js";
import ProjectService from "../../services/projectService.js";
import RenameModal from "./RenameModal.vue";
import ConfirmModal from "./ConfirmModal.vue";

const props = defineProps({
  // { id, name, description, userRole, permissions }
  project: { type: Object, required: true },
});
const emit = defineEmits(["renamed", "duplicated", "deleted", "left"]);

const route = useRoute();
const router = useRouter();
const $auth0 = inject("$auth0");
const projectService = new ProjectService();
const { open: openShare } = useShareModal();
const { refreshAll: refreshProjects } = useProjectsByTab();

const { isOpen, triggerEl, menuEl, menuStyle, toggle, close, onMenuKeydown } = useMenu();

const canShare = computed(() => hasPermission(props.project.permissions, COLLABORATOR_PERMISSIONS.SHARE_PROJECT));
const canRename = computed(() => hasPermission(props.project.permissions, COLLABORATOR_PERMISSIONS.RENAME_PROJECT));
// Duplicate is deliberately blocked for Contributor/Viewer regardless of the raw
// permission array, mirroring the legacy app's rule (see FoliaContextMenu).
const canDuplicate = computed(() =>
  !["CONTRIBUTOR", "VIEWER"].includes(props.project.userRole)
  && hasPermission(props.project.permissions, COLLABORATOR_PERMISSIONS.DUPLICATE_PROJECT)
);
const canDelete = computed(() => hasPermission(props.project.permissions, COLLABORATOR_PERMISSIONS.DELETE_PROJECT));
const canLeave = computed(() => props.project.userRole !== "OWNER");

function onShare() {
  close();
  openShare({ resourceId: props.project.id, resourceName: props.project.name });
}

// ─── Rename ─────────────────────────────────────────────────────────────────
const showRename = ref(false);
const isRenaming = ref(false);
function onRenameClick() { close(); showRename.value = true; }
async function saveRename({ name, description }) {
  isRenaming.value = true;
  try {
    await projectService.editProject({ projectId: props.project.id, name, description });
    props.project.name = name;
    props.project.description = description;
    showRename.value = false;
    emit("renamed", { name, description });
  } finally {
    isRenaming.value = false;
  }
}

// ─── Duplicate ──────────────────────────────────────────────────────────────
const isDuplicating = ref(false);
async function onDuplicate() {
  close();
  if (isDuplicating.value) return;
  isDuplicating.value = true;
  try {
    await projectService.duplicateProject(props.project.id);
    await refreshProjects();
    emit("duplicated");
  } finally {
    isDuplicating.value = false;
  }
}

// ─── Delete ─────────────────────────────────────────────────────────────────
const showDelete = ref(false);
const isDeleting = ref(false);
function onDeleteClick() { close(); showDelete.value = true; }
async function confirmDelete() {
  isDeleting.value = true;
  try {
    await projectService.deleteProject(props.project.id);
    showDelete.value = false;
    await refreshProjects();
    emit("deleted");
    if (route.params.projectId === props.project.id) router.push({ name: "v2-projects" });
  } finally {
    isDeleting.value = false;
  }
}

// ─── Leave project (non-owners) ────────────────────────────────────────────
const showLeave = ref(false);
const isLeaving = ref(false);
function onLeaveClick() { close(); showLeave.value = true; }
async function confirmLeave() {
  isLeaving.value = true;
  try {
    const user = await $auth0.getUser();
    await projectService.shareProject({
      projectId: props.project.id,
      collaboratorEmail: user.profile.email,
      collaboratorRole: null,
    });
    showLeave.value = false;
    await refreshProjects();
    emit("left");
    if (route.params.projectId === props.project.id) router.push({ name: "v2-projects" });
  } finally {
    isLeaving.value = false;
  }
}
</script>

<template>
  <div class="relative shrink-0" @click.stop>
    <button
      ref="triggerEl"
      class="flex items-center justify-center w-7 h-7 rounded text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer transition-colors"
      aria-haspopup="menu"
      :aria-expanded="isOpen"
      aria-label="Project options"
      @click="toggle"
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
      </svg>
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="menuEl"
        role="menu"
        aria-label="Project options"
        :style="menuStyle"
        class="w-52 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1.5 z-[400] overflow-hidden"
        @keydown="onMenuKeydown"
      >
        <button v-if="canShare" role="menuitem" class="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] text-left border-0 bg-transparent cursor-pointer transition-colors text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700" @click="onShare">
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M14.336 9.125H16.4615C16.6494 9.125 16.8296 9.19401 16.9625 9.31685C17.0954 9.43968 17.17 9.60628 17.17 9.78V16.64C17.17 16.8137 17.0954 16.9803 16.9625 17.1032C16.8296 17.226 16.6494 17.295 16.4615 17.295H3.7085C3.52059 17.295 3.34038 17.226 3.20751 17.1032C3.07465 16.9803 3 16.8137 3 16.64V9.78C3 9.60628 3.07465 9.43968 3.20751 9.31685C3.34038 9.19401 3.52059 9.125 3.7085 9.125H5.834" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /><path d="M10.082 2.29111V11.8359" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /><path d="M13.4925 5.70117L10.0837 2.2923L6.6748 5.70117" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /></svg>
          Share Project
        </button>
        <button v-if="canRename" role="menuitem" class="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] text-left border-0 bg-transparent cursor-pointer transition-colors text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700" @click="onRenameClick">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>
          Edit Project Details
        </button>
        <button v-if="canDuplicate" role="menuitem" :disabled="isDuplicating" class="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] text-left border-0 bg-transparent cursor-pointer transition-colors text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50" @click="onDuplicate">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="8" y="8" width="12" height="12" rx="2" stroke="currentColor" stroke-width="1.8" /><path d="M4 16V6a2 2 0 0 1 2-2h10" stroke="currentColor" stroke-width="1.8" /></svg>
          {{ isDuplicating ? "Duplicating…" : "Duplicate Project" }}
        </button>
        <div v-if="(canShare || canRename || canDuplicate) && (canDelete || canLeave)" class="h-px bg-slate-100 dark:bg-slate-700 my-1.5 mx-2" />
        <button v-if="canLeave" role="menuitem" class="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] text-left border-0 bg-transparent cursor-pointer transition-colors text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30" @click="onLeaveClick">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /><polyline points="16 17 21 12 16 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /><line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>
          Leave Project
        </button>
        <button v-if="canDelete" role="menuitem" class="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] text-left border-0 bg-transparent cursor-pointer transition-colors text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30" @click="onDeleteClick">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6h16z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>
          Delete Project
        </button>
      </div>
    </Teleport>

    <RenameModal
      v-if="showRename"
      title="Edit Project Details"
      :initial-name="project.name"
      :show-description="true"
      :initial-description="project.description ?? ''"
      :is-saving="isRenaming"
      @close="showRename = false"
      @save="saveRename"
    />

    <ConfirmModal
      v-if="showDelete"
      title="Delete Project?"
      :message="`This permanently deletes “${project.name}” and all of its documents. This can't be undone.`"
      confirm-label="Delete Project"
      busy-label="Deleting…"
      :is-busy="isDeleting"
      @close="showDelete = false"
      @confirm="confirmDelete"
    />

    <ConfirmModal
      v-if="showLeave"
      title="Leave Project?"
      :message="`You'll lose access to “${project.name}” until someone invites you back.`"
      confirm-label="Leave Project"
      busy-label="Leaving…"
      :is-busy="isLeaving"
      @close="showLeave = false"
      @confirm="confirmLeave"
    />
  </div>
</template>
